import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Search, X } from 'lucide-react';
import type {
  MarketingRequest,
  MarketingRequestPriority,
  MarketingRequestStatus,
  MarketingRequestType,
} from '../../types/marketing';

const TYPE_LABELS: Record<MarketingRequestType, string> = {
  content: 'Content',
  social: 'Social',
  email: 'Email',
  graphic: 'Graphic',
  podcast: 'Podcast',
  website: 'Website',
  other: 'Other',
};

const STATUS_LABELS: Record<MarketingRequestStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting',
  done: 'Done',
  cancelled: 'Cancelled',
};

const PRIORITY_ORDER: Record<MarketingRequestPriority, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
};

interface MarketingRequestTableProps {
  requests: MarketingRequest[];
  onSelectRequest: (request: MarketingRequest) => void;
  emptyMessage?: string;
}

const MarketingRequestTable: React.FC<MarketingRequestTableProps> = ({
  requests,
  onSelectRequest,
  emptyMessage = 'No content requests yet.',
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = [...requests];
    rows.sort((a, b) => {
      const statusRank = (s: MarketingRequestStatus) =>
        s === 'open' || s === 'in_progress' || s === 'waiting' ? 0 : 1;
      const sr = statusRank(a.status) - statusRank(b.status);
      if (sr !== 0) return sr;
      const pr = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      if (pr !== 0) return pr;
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });
    if (!q) return rows;
    return rows.filter(r => {
      const hay = [
        r.title,
        r.description || '',
        r.requestedByName,
        TYPE_LABELS[r.requestType],
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [requests, search]);

  if (requests.length === 0) {
    return <div className="mh-requests-empty">{emptyMessage}</div>;
  }

  return (
    <div className="mh-requests-table-wrap">
      <div className="mh-requests-search">
        <Search size={14} aria-hidden />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search requests…"
          aria-label="Search content requests"
        />
        {search && (
          <button type="button" className="mh-requests-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="mh-requests-empty">No requests match your filters.</div>
      ) : (
        <table className="mh-requests-table">
          <thead>
            <tr>
              <th className="mh-req-th mh-req-th--title">Title</th>
              <th className="mh-req-th">Type</th>
              <th className="mh-req-th">Status</th>
              <th className="mh-req-th">Priority</th>
              <th className="mh-req-th">Due</th>
              <th className="mh-req-th">Requested by</th>
              <th className="mh-req-th">Updated</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr
                key={r.id}
                className="mh-requests-row"
                onClick={() => onSelectRequest(r)}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelectRequest(r);
                  }
                }}
              >
                <td className="mh-requests-title">{r.title}</td>
                <td><span className="mh-req-type">{TYPE_LABELS[r.requestType]}</span></td>
                <td><span className={`mh-req-status mh-req-status--${r.status}`}>{STATUS_LABELS[r.status]}</span></td>
                <td><span className={`mh-req-priority mh-req-priority--${r.priority}`}>{r.priority}</span></td>
                <td>{r.dueDate ? format(r.dueDate.toDate(), 'MMM d, yyyy') : '—'}</td>
                <td>{r.requestedByName}</td>
                <td>{format(r.updatedAt.toDate(), 'MMM d')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MarketingRequestTable;
